<template>
  <div class="file-manager">
    <!-- Top bar -->
    <div class="file-manager__top-bar">
      <SBtn
        v-quick-title="$t('message.CLOSE')"
        dark
        icon
        @click="close()"
      >
        <Icon image :size="30">{{ "/images/close-button-white.png" }}</Icon>
      </SBtn>
      <div
        class="action-btn"
        style="margin: 6px 8px;"
        @click="onClickAddFile"
      >
        <Icon image left :size="14">{{ "/images/add-white.png" }}</Icon>
        {{ $t('message.ADD_FILES') }}
      </div>
      <div class="file-manager__spacer" />
      <SBtn
        v-if="hasSingleSelection"
        v-quick-title="$t('message.DOWNLOAD')"
        dark
        icon
        @click="onClickDownload"
      >
        <Icon image>{{ iconDownloadOutline }}</Icon>
      </SBtn>
      <SBtn
        v-if="hasSelection"
        v-quick-title="$t('message.REMOVE')"
        dark
        icon
        @click="onClickDelete"
      >
        <Icon image>{{ iconTrashCan }}</Icon>
      </SBtn>
      <!--div v-if="hasSelection" class="file-manager__separator" /-->
    </div>
    <!-- END Top bar -->
    <!-- View -->
    <div class="file-manager__view">
      <GridView
        :files="fileList"
        :selected="fileIdsSelected"
        @fileClick="onFileClick"
        @voidClick="onVoidClick"
      />
      <SBtn
        class="file-manager__confirm"
        color="green"
        absolute
        dark
        icon
        large
        @click="onConfirm"
      >
        <Icon image="">
          {{ "/images/tick-white.png" }}
        </Icon>
      </SBtn>
    </div>
    <!-- END View -->
    <!-- Hidden components -->
    <input
      v-show="false"
      ref="fileInput"
      type="file"
      :accept="fileTypeAccepted"
      multiple
      @change="onChangeFileInput"
    >

    <a
      v-show="false"
      ref="fileDownload"
      :href="fileDataToDownload"
      :download="fileNameToDownload"
    />
  </div>
</template>

<script>
/**
 * File manager - Manage and select file from the system
 *
 * @vue-prop {Number} [max=Infinity] - Maximum files that can be selected
 * @vue-prop {String} [pathname] - Pathname that will be associated if files are uploaded.
 * @vue-prop {Boolean|Number[]} [value] - Activate selection mode ; List of file ID already selected
 * @vue-event {Object[]} change - Triggered on file select/unselect
 *                                Emit list of file information selected
 * @vue-event {} close - Triggered when file manager need to be closed
 * @vue-event {Object[]} input - Triggered when confirm button is clicked
 *                               Emit list of file information selected
 */
import $ from 'jquery';
import toastr from 'toastr';
// Import components
import GridView from '@/components/file-system/fm-grid-view.vue';
import Icon from '@/components/icon.vue';
import SBtn from '@/components/s-btn.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import Vuex utils
import { SEARCH_FILE_DETAILS, UPLOAD_FILE, DELETE_FILE } from '@/store/modules/file/action-types';
// Import utils
import {
  FILE_TYPE_ACCEPTED,
  ACCEPTED_DATA_TYPES,
  ACCEPTED_PATHNAMES,
} from '@/utils/file';
import { calert } from '@/utils/main';

const iconDownloadOutline = require('@/assets/img/download-medium-white.png');
const iconTrashCan = require('@/assets/img/delete-medium-white.png');


export default {
  name: 'FileManager',

  components: {
    GridView,
    Icon,
    SBtn,
  },

  mixins: [mixinSocketHandler],

  props: {
    max: {
      type: Number,
      default: Infinity,
    },
    pathname: {
      type: String,
      default: '',
      validator: v => ACCEPTED_PATHNAMES.includes(v),
    },
    value: {
      type: [Boolean, Array],
      default: false,
      validator: v => typeof v === 'boolean' || v.every(id => typeof id === 'number' && id > 0),
    },
  },

  data() {
    return {
      iconDownloadOutline,
      iconTrashCan,
      fileList: [],
      fileIdsSelected: [],
      /** File viewer */
      fileViewerVisible: false,
      fileViewerIndex: 0,
      // Details of the selected file when clicking 'download'
      fileDataToDownload: '',
      fileNameToDownload: '',
    };
  },

  computed: {
    fileTypeAccepted() {
      return Object.values(FILE_TYPE_ACCEPTED).map(v => (v instanceof Array ? v.join(',') : v)).join(',');
    },
    filesSelected() {
      return this.fileIdsSelected.map(id => (
        Object.assign({ id }, this.fileList.find(f => f.id === id))
      ));
    },
    hasSelection() {
      return this.fileIdsSelected.length > 0;
    },
    hasSingleSelection() {
      return this.fileIdsSelected.length === 1;
    },
    searchQueries() {
      return {
        // TODO Add filter pannel
      };
    },
  },

  watch: {
    value() {
      this.syncSelection();
    },
  },

  created() {
    this.syncSelection();
    this.fetchFileList();
  },

  methods: {
    /**
     * Fetch file details, according to filters
     * @return {Promise} HTTP request promise
     */
    async fetchFileList() {
      return this.$store.dispatch(`file/${SEARCH_FILE_DETAILS}`, this.searchQueries)
        .then((files) => {
          this.fileList = files.reverse();
          return Promise.resolve();
        })
        .catch(() => {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        });
    },
    /**
     * Synchronize `value` props with the internal file selection
     */
    syncSelection() {
      if (this.value instanceof Array) {
        this.fileIdsSelected = this.value.slice();
      }
    },
    /**
     * Toggle file from the selection
     * @param {Number} fileId - ID of the file selected
     * @param {Boolean} flag - true will add, false will remove
     */
    selectFile(fileId, flag) {
      if (!this.value) {
        this.unselectAllFiles();
      }
      const idx = this.fileIdsSelected.indexOf(fileId);
      if (idx === -1 && flag !== false) {
        if (this.fileIdsSelected.length < this.max) {
          this.fileIdsSelected.push(fileId);
        }
      } else if (flag !== true) {
        this.fileIdsSelected.splice(idx, 1);
      }
      this.$emit('input', this.filesSelected);
    },
    /**
     * Remove all files from the selection
     */
    unselectAllFiles() {
      this.fileIdsSelected = [];
    },
    /**
     * Upload files on the system ; Will synchronize with the vuex store
     * @param {FileList} files - List of files, probably given by an HTMLInput
     * @return {Promise} Promise, fulfilled when requests are all finished
     */
    async uploadFiles(files) {
      const promises = [];

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const formData = new FormData();

        formData.append('MAX_FILE_SIZE', 20000000);
        if (this.pathname) {
          formData.append('pathname', this.pathname);
        }
        promises.push(
          this.$store.dispatch(`file/${UPLOAD_FILE}`, { file, pathname: this.pathname })
            .catch(() => -1),
        );
      }
      return Promise.all(promises)
        .then((results) => {
          const fileDetails = results.filter(r => r !== -1);
          const nb = fileDetails.length;
          if (nb > 0) {
            toastr.success(this.$tc('message.FILE_UPLOAD_SUCCESS', nb, { nb }));
          }
          this.fetchFileList();
          return fileDetails;
        });
    },
    /**
     * Delete files on the system ; Will synchronize with the vuex store
     * @param {Object[]} files - List of files ; must contain file ids
     * @return {Promise} Promise, fulfilled when requests are all finished
     */
    async deleteFiles(files) {
      const promises = [];
      const toRefresh = {};
      ACCEPTED_DATA_TYPES.forEach((type) => { toRefresh[type] = new Set(); });

      files.forEach((file) => {
        file.refs.forEach((r) => {
          toRefresh[r.type].add(r.id);
        });
        promises.push(
          this.$store.dispatch(`file/${DELETE_FILE}`, { id: file.id })
            .catch(() => -1),
        );
      });
      return Promise.all(promises)
        .then((results) => {
          const fileIds = results.filter(r => r > 0);
          const nb = fileIds.length;
          if (nb > 0) {
            toastr.success(this.$tc('message.FILE_DELETE_SUCCESS', nb, { nb }));
          }
          this.fetchFileList();
          if (toRefresh.information.size > 0) {
            this.sendSocketMessage({ socketCompany: 1, msg: SocketMessages.CC_MSGS.MEETING_INFOS });
          }
          if (toRefresh.action.size > 0) {
            toRefresh.action.forEach((id) => {
              this.sendSocketMessage({ socketCompany: 1, id, msg: SocketMessages.CC_MSGS.ACTION_EDITION });
            });
          }
          if (toRefresh.problem.size > 0) {
            toRefresh.problem.forEach((id) => {
              this.sendSocketMessage({ socketCompany: 1, id, msg: SocketMessages.CC_MSGS.PROBLEM_SHEET });
            });
          }
        });
    },
    /**
     * Close the overlay
     */
    close() {
      this.$emit('close');
      this.syncSelection(); // Reset selection
    },
    /**
     * ==================
     *   EVENT HANDLERS
     * ==================
     */
    onFileClick({ file }) {
      this.selectFile(file.id);
    },
    onVoidClick() {
      if (!this.value) {
        this.unselectAllFiles();
      }
    },
    onClickDownload() {
      // TODO Handle multiple file selection ; Build a zip file of these files
      const fileId = this.fileIdsSelected[0];
      if (!fileId) return;
      this.fileNameToDownload = this.$store.state.file.files[`${fileId}`].name;
      this.fileDataToDownload = this.$store.state.file.fileData[`${fileId}`];
      this.$nextTick(() => {
        this.$refs.fileDownload.click();
      });
    },
    onClickDelete() {
      let message = this.$tc('message.FILE_DELETE_CONFIRM', this.filesSelected.length);
      message += this.filesSelected.reduce((acc, cur) => (
        `${acc}\n • ${cur.name} (${this.$tc('message.FILE_REF_COUNT', cur.refs.length, { nb: cur.refs.length })})`
      ), '');
      calert(message, [
        {
          title: this.$t('message.DELETE'),
          action: () => {
            this.deleteFiles(this.filesSelected);
            this.unselectAllFiles();
          },
          sClass: 'red',
        },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    onClickAddFile() {
      $(this.$refs.fileInput).trigger('click');
    },
    onChangeFileInput() {
      const { files } = this.$refs.fileInput;
      this.uploadFiles(files)
        .then(results => results.forEach(file => this.selectFile(file.id)));
      this.$refs.fileInput.value = ''; // Clear files
    },
    onConfirm() {
      this.$emit('change', this.filesSelected);
      this.close();
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.file-manager {
  position: fixed;
  top: 65px;
  left: $nav-width;
  bottom: 0px;
  right: 0px;
  background: rgba(0, 0, 0, 0.87);
  box-sizing: border-box;
  overflow: hidden;
  white-space: initial; /* Prevent any page from applying nowrap */
  z-index: 10017;
  align-items: stretch;
  display: flex;
  flex-direction: column;

  * {
    box-sizing: border-box;
  }
  &__top-bar {
    align-items: center;
    border-bottom: 1px solid #dadce0;
    display: flex;
    flex: 0 1 41px;

    .s-btn--icon {
      margin: 2px;
    }
  }
  &__spacer {
    flex-grow: 1;
  }
  &__separator {
    border-right: 1px solid #e5e5e5;
    height: calc(100% - 12px);
    margin: 4px;
  }
  &__view {
    flex: 1 1 auto;
    height: 100%;
    overflow: auto;
    width: 100%;
  }
  &__confirm {
    bottom: 30px;
    left: 0;
    right: 0;
    margin-left: auto;
    margin-right: auto;
  }
}

@media (max-width: $smartphone-landscape) {
  .file-manager {
    top: $smartphone-header-height;
    left: $smartphone-nav-width;
  }
}
</style>
